import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Link,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Table } from "../../common/table/Table";
import { REPORT_OTP_ATTEMPTS, REPORT_OTP_FAILURES } from "./otpFailures.queries";
import { formatCount, formatDateTime, formatPercent, formatPeriod } from "./reportFormat";
import { rangeVariables, ReportFilterState } from "./ReportFilters";
import { OtpChannel } from "./reportParams";
import { MemberLink, ReportError, Section, StatTile, TileRow } from "./reportParts";

/** Whose side a failure is on, as the API's OtpFailureSide says. */
export const SIDE_LABELS: Record<string, string> = {
  MEMBER: "Member's number",
  OUR_RULES: "Our rules",
  OUR_SETUP: "Our setup",
  TWILIO: "Twilio",
  NONE: "",
};

const SIDE_COLORS: Record<string, "default" | "warning" | "error" | "info"> = {
  MEMBER: "default",
  OUR_RULES: "info",
  OUR_SETUP: "error",
  TWILIO: "warning",
};

const isFailure = (reason: any) => reason.category === "TWILIO_ERROR" || reason.category === "BLOCKED";

const ATTEMPT_COLUMNS = [
  { label: "When", key: "createdAt", skipSorting: true, minWidth: "170px", rowRender: (row: any) => formatDateTime(row.createdAt) },
  { label: "Number or email", key: "destination", skipSorting: true, rowRender: (row: any) => row.destination },
  { label: "Country", key: "countryName", skipSorting: true, rowRender: (row: any) => row.countryName ?? "" },
  {
    label: "What happened",
    key: "label",
    skipSorting: true,
    minWidth: "220px",
    rowRender: (row: any) => (
      <Box>
        <Box>{row.label}</Box>
        {row.detail && (
          <Typography variant="caption" color="text.secondary">
            {row.detail}
          </Typography>
        )}
      </Box>
    ),
  },
  { label: "Account", key: "member", skipSorting: true, rowRender: (row: any) => <MemberLink member={row.member} /> },
  { label: "IP", key: "ipAddress", skipSorting: true, rowRender: (row: any) => row.ipAddress ?? "" },
];

/**
 * Why sign-in codes were not sent: every refusal by Twilio (with its error code)
 * or by our own rules, explained, counted, by country and over time, and each
 * request listed. Backend: docs/OTP_FAILURES_REPORT.md. Region filters do not
 * apply: most failed requests come from people with no account.
 */
export const OtpFailuresTab: React.FC<{ filters: ReportFilterState; channel: OtpChannel; destination: string }> = ({
  filters,
  channel,
  destination,
}) => {
  const [outcome, setOutcome] = useState<string | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const range = rangeVariables(filters);
  const channelVariable = channel === "all" ? null : channel;

  const { data, loading, error } = useQuery(REPORT_OTP_FAILURES, {
    variables: { range, channel: channelVariable },
    fetchPolicy: "cache-and-network",
  });

  // A new question starts on its first page; derived, as on the Events tab.
  const question = JSON.stringify([range, channel, outcome, destination, rowsPerPage]);
  const [paging, setPaging] = useState({ question, page: 0 });
  const page = paging.question === question ? paging.page : 0;
  const setPage = (next: number) => setPaging({ question, page: next });

  const attempts = useQuery(REPORT_OTP_ATTEMPTS, {
    variables: {
      range,
      filter: {
        channel: channelVariable,
        outcome,
        // With nothing picked the list is the failures; a search shows a number's whole story, sent codes too.
        failedOnly: !outcome && !destination,
        destination: destination || null,
      },
      page: { take: rowsPerPage, skip: page * rowsPerPage },
    },
    fetchPolicy: "cache-and-network",
  });

  const report = data?.reportOtpFailures;
  const totals = report?.totals;
  const failures: any[] = (report?.reasons ?? []).filter(isFailure);
  const picked = (report?.reasons ?? []).find((reason: any) => reason.outcome === outcome);

  return (
    <Stack spacing={2}>
      <ReportError error={error} />
      {loading && <LinearProgress />}

      {totals && (
        <Section
          title="Sign-in codes"
          note="Every request for a code in the range. Sent means Twilio accepted it, not that the phone received it: a carrier can still drop an accepted text."
        >
          <TileRow>
            <StatTile label="Requests" value={formatCount(totals.attempts)} caption={`${formatCount(totals.test)} from test identities`} />
            <StatTile label="Sent" value={formatCount(totals.sent)} />
            <StatTile label="Refused by Twilio" value={formatCount(totals.twilioErrors)} caption="Has a Twilio error code" />
            <StatTile label="Stopped by our rules" value={formatCount(totals.blocked)} caption="Limits, reCAPTCHA, blocks" />
            <StatTile label="Not sent" value={formatPercent(totals.failureRate)} caption="Of requests, test identities aside" />
            <StatTile
              label="Never got a code"
              value={formatCount(totals.destinationsNeverSent)}
              caption={`of ${formatCount(totals.destinations)} numbers and emails`}
            />
          </TileRow>
        </Section>
      )}

      {report && (
        <Section title="Why codes were not sent" note="Most frequent first. Click a reason to see what it means and list its requests.">
          {failures.length === 0 ? (
            <Typography color="text.secondary">No failed requests in this range.</Typography>
          ) : (
            <Box sx={{ overflow: "auto" }}>
              <MuiTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reason</TableCell>
                    <TableCell>Whose side</TableCell>
                    <TableCell>Twilio code</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Numbers / emails</TableCell>
                    <TableCell align="right">Text</TableCell>
                    <TableCell align="right">Email</TableCell>
                    <TableCell>Last</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failures.map((reason) => (
                    <TableRow
                      key={reason.outcome}
                      hover
                      selected={reason.outcome === outcome}
                      onClick={() => setOutcome(reason.outcome === outcome ? null : reason.outcome)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{reason.label}</TableCell>
                      <TableCell>
                        <Chip size="small" label={SIDE_LABELS[reason.side]} color={SIDE_COLORS[reason.side] ?? "default"} variant="outlined" />
                      </TableCell>
                      <TableCell>{reason.twilioCode ?? ""}</TableCell>
                      <TableCell align="right">{formatCount(reason.attempts)}</TableCell>
                      <TableCell align="right">{formatCount(reason.destinations)}</TableCell>
                      <TableCell align="right">{formatCount(reason.sms)}</TableCell>
                      <TableCell align="right">{formatCount(reason.email)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(reason.lastAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </MuiTable>
            </Box>
          )}
          {picked && <ReasonDetail reason={picked} />}
        </Section>
      )}

      <Section
        title={picked ? `Requests: ${picked.label}` : destination ? `Requests for ${destination}` : "Failed requests"}
        note="Newest first. Account is whoever holds that number or email now. Search applies when you press Enter."
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
          {picked && <Chip label={picked.label} onDelete={() => setOutcome(null)} />}
          {attempts.data && (
            <Typography variant="body2" color="text.secondary">
              {formatCount(attempts.data.reportOtpAttempts.total)} requests
            </Typography>
          )}
        </Box>
        <ReportError error={attempts.error} />
        <Table
          loading={attempts.loading}
          rows={attempts.data?.reportOtpAttempts.attempts ?? []}
          columns={ATTEMPT_COLUMNS}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          total={attempts.data?.reportOtpAttempts.total ?? 0}
        />
      </Section>

      {report && report.countries.length > 0 && (
        <Section title="Text messages by country" note="From the number's dialling code. +1 is shared by the US, Canada and the Caribbean, so it is shown as the code.">
          <Box sx={{ overflow: "auto", maxHeight: 420 }}>
            <MuiTable size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Country</TableCell>
                  <TableCell align="right">Requests</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">Refused by Twilio</TableCell>
                  <TableCell align="right">Our rules</TableCell>
                  <TableCell>Commonest failure</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.countries.map((country: any) => (
                  <TableRow key={`${country.country}-${country.dialCode}-${country.name}`}>
                    <TableCell>{country.name}</TableCell>
                    <TableCell align="right">{formatCount(country.attempts)}</TableCell>
                    <TableCell align="right">{formatCount(country.sent)}</TableCell>
                    <TableCell align="right">{formatCount(country.twilioErrors)}</TableCell>
                    <TableCell align="right">{formatCount(country.blocked)}</TableCell>
                    <TableCell>{country.topFailure ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </Box>
        </Section>
      )}

      {report && (
        <Section title="Over time">
          <Box sx={{ overflow: "auto", maxHeight: 420 }}>
            <MuiTable size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Requests</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">Refused by Twilio</TableCell>
                  <TableCell align="right">Our rules</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...report.periods].reverse().map((period: any) => (
                  <TableRow key={period.period}>
                    <TableCell>{formatPeriod(period.period, filters.granularity)}</TableCell>
                    <TableCell align="right">{formatCount(period.attempts)}</TableCell>
                    <TableCell align="right">{formatCount(period.sent)}</TableCell>
                    <TableCell align="right">{formatCount(period.twilioErrors)}</TableCell>
                    <TableCell align="right">{formatCount(period.blocked)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </Box>
        </Section>
      )}
    </Stack>
  );
};

/** What a reason means, what the member saw, and what to do. All of it is the API's wording. */
const ReasonDetail: React.FC<{ reason: any }> = ({ reason }) => (
  <Alert severity={reason.side === "OUR_SETUP" ? "error" : "info"} sx={{ mt: 2 }}>
    <Typography variant="subtitle2">
      {reason.label}
      {reason.twilioCode && ` (Twilio ${reason.twilioCode})`}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.5 }}>
      {reason.explanation}
    </Typography>
    <Typography variant="body2" sx={{ mt: 1 }}>
      <b>What the member saw:</b> {reason.memberSaw}
    </Typography>
    {reason.action && (
      <Typography variant="body2" sx={{ mt: 1 }}>
        <b>What to do:</b> {reason.action}
      </Typography>
    )}
    {reason.docsUrl && (
      <Typography variant="body2" sx={{ mt: 1 }}>
        <Link href={reason.docsUrl} target="_blank" rel="noopener noreferrer">
          Twilio's page for this code
        </Link>
      </Typography>
    )}
  </Alert>
);
